import {useCallback, useMemo, useRef, useState, type ReactNode} from 'react'
import {createPortal} from 'react-dom'
import ToastItem from './ToastItem'
import {ToastContext} from './toastContext'
import {
  DEFAULT_ERROR_MS,
  DEFAULT_INFO_MS,
  DEFAULT_SUCCESS_MS,
  type Toast,
  type ToastApi,
  type ToastVariant,
} from './toastTypes'

interface ToastProviderProps {
  children: ReactNode
}

// Suppress the same toast (variant+message) when it fires again within this window.
// Protects against React StrictMode double-effects, accidental re-renders, and
// rapid-fire identical failures from background polling.
const DEDUPE_MS = 2000

export const ToastProvider = ({children}: ToastProviderProps) => {
  const [toasts, setToasts] = useState<Toast[]>([])
  const counterRef = useRef(0)
  const recentRef = useRef<Map<string, number>>(new Map())

  const push = useCallback((variant: ToastVariant, message: string, durationMs: number) => {
    const key = `${variant} ${message}`
    const now = Date.now()
    const last = recentRef.current.get(key)
    if (last !== undefined && now - last < DEDUPE_MS) return
    recentRef.current.set(key, now)
    // Best-effort cleanup so the map doesn't grow unbounded.
    if (recentRef.current.size > 32) {
      for (const [k, ts] of recentRef.current) {
        if (now - ts > DEDUPE_MS) recentRef.current.delete(k)
      }
    }
    counterRef.current += 1
    const id = `t${counterRef.current}-${now}`
    setToasts((prev) => [...prev, {id, variant, message, durationMs}])
  }, [])

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const api = useMemo<ToastApi>(
    () => ({
      success: (message, durationMs = DEFAULT_SUCCESS_MS) => push('success', message, durationMs),
      error: (message, durationMs = DEFAULT_ERROR_MS) => push('error', message, durationMs),
      info: (message, durationMs = DEFAULT_INFO_MS) => push('info', message, durationMs),
      dismiss,
    }),
    [push, dismiss],
  )

  return (
    <ToastContext.Provider value={api}>
      {children}
      {typeof document !== 'undefined' &&
        createPortal(
          <div style={styles.container} aria-live="polite">
            {toasts.map((t) => (
              <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
            ))}
          </div>,
          document.body,
        )}
    </ToastContext.Provider>
  )
}

const styles = {
  container: {
    position: 'fixed',
    bottom: 24,
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    alignItems: 'center',
    zIndex: 2147483000,
    pointerEvents: 'none',
  },
} as const
